import React from "react";
import {
  DialogContent,
  Button,
  TextField,
  Box,
  CircularProgress,
  InputAdornment,
  useTheme,
  Stack,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import DescriptionIcon from "@mui/icons-material/Description";
import CustomDatePicker from "../../utils/DatePicker/CustomDatePicker";
import { styled } from "@mui/material/styles";
import BaseDialog from "../../utils/dialog/BaseDialog";
import { InfoOutlined } from "@mui/icons-material";
import ConfirmationDialog from "../RecycleBin/ConfirmationDialog";
import { buildFinancialYearEndDate } from "../../../utils/hooks/financialYear";

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: "12px",
  textTransform: "none",
  fontWeight: 600,
  minWidth: 120,
  fontSize: "0.95rem",
  boxShadow: "none",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    transform: "translateY(-1px)",
  },
  transition: "all 0.2s ease-in-out",
}));

const CompanyDialog = ({
  type, // 'create', 'edit', or 'delete'
  open,
  onClose,
  subscription,
  companyData = {},
  onConfirm,
  dialogLoading = false,
  setCompanyName,
  setUEN,
  setFinancialYearEnd,
  setYearOfAssessment,
  setIsRestricted,
}) => {
  const theme = useTheme();
  const {
    name = "",
    uen = "",
    financialYearEnd,
    yearOfAssessment = "",
    isRestricted = false,
  } = companyData || {};

  const parsedFinancialYearEnd = financialYearEnd
    ? new Date(financialYearEnd)
    : null;
  const hasValidFinancialYearEnd =
    parsedFinancialYearEnd && !Number.isNaN(parsedFinancialYearEnd.getTime());
  const fyeYear = Number(yearOfAssessment)
    ? Number(yearOfAssessment) - 1
    : null;

  const getTitle = () => {
    switch (type) {
      case "create":
        return "Add Company";
      case "edit":
        return "Edit Company";
      case "delete":
        return "Delete Company";
      default:
        return "";
    }
  };

  const outlinedFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderWidth: 2,
      },
    },
    "& .MuiInputLabel-root": {
      fontWeight: 500,
    },
  };

  const renderContent = () => {
    return (
      <Stack direction='column' spacing={3}>
        <Box sx={{ width: "100%" }}>
          <TextField
            autoFocus
            fullWidth
            id='name'
            label='Company Name'
            variant='outlined'
            value={name}
            onChange={(e) => setCompanyName(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <BusinessIcon sx={{ color: "action.active" }} />
                </InputAdornment>
              ),
            }}
            inputProps={{
              autocomplete: "off",
            }}
            sx={{
              mt: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: 12,
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.primary.main,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderWidth: 2,
                },
              },
              "& .MuiInputLabel-root": {
                fontWeight: 500,
              },
            }}
          />
        </Box>

        <Box sx={{ width: "100%" }}>
          <TextField
            fullWidth
            id='uen'
            label='Unique Entity Number (UEN)'
            variant='outlined'
            value={String(uen ?? "")}
            onChange={(e) => setUEN(String(e.target.value))}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <DescriptionIcon sx={{ color: "action.active" }} />
                </InputAdornment>
              ),
            }}
            inputProps={{
              autocomplete: "off",
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 12,
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.primary.main,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderWidth: 2,
                },
              },
              "& .MuiInputLabel-root": {
                fontWeight: 500,
              },
            }}
          />
        </Box>

        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "flex-start",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <CustomDatePicker
              label='First Year of Assessment'
              selected={
                yearOfAssessment
                  ? new Date(Number(yearOfAssessment), 0, 1)
                  : null
              }
              size='large'
              onChange={(date) => {
                const newYear = date ? date.getFullYear() : "";
                setYearOfAssessment(newYear);
                if (hasValidFinancialYearEnd && newYear) {
                  setFinancialYearEnd(
                    buildFinancialYearEndDate(
                      newYear - 1,
                      parsedFinancialYearEnd.getMonth(),
                      parsedFinancialYearEnd.getDate(),
                    ),
                  );
                }
              }}
              showYearPicker
              dateFormat='yyyy'
              sx={{
                width: "100%",
                ...outlinedFieldSx,
              }}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: 200 }}>
            <CustomDatePicker
              label='Financial Year End'
              selected={
                hasValidFinancialYearEnd
                  ? fyeYear
                    ? buildFinancialYearEndDate(
                        fyeYear,
                        parsedFinancialYearEnd.getMonth(),
                        parsedFinancialYearEnd.getDate(),
                      )
                    : parsedFinancialYearEnd
                  : null
              }
              size='large'
              disabled={!yearOfAssessment}
              minDate={fyeYear ? new Date(fyeYear, 0, 1) : undefined}
              maxDate={fyeYear ? new Date(fyeYear, 11, 31) : undefined}
              onChange={(date) => setFinancialYearEnd(date)}
              dateFormat='d MMMM yyyy'
              helperText={
                !yearOfAssessment
                  ? "Select the Year of Assessment first."
                  : "Select the exact year-end date."
              }
              sx={{
                width: "100%",
                ...outlinedFieldSx,
              }}
            />
          </Box>
        </Box>

        {subscription?.plan_name === "Tax Professional" && (
          <Box sx={{ width: "100%", display: "flex", alignItems: "center" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isRestricted}
                  onChange={(e) => setIsRestricted(e.target.checked)}
                  color='primary'
                />
              }
              label='Hide this company from new users'
              sx={{ ml: 0 }}
            />
            <Tooltip title='When enabled, newly created users will not have access to this company unless assigned manually.'>
              <InfoOutlined
                color='info'
                sx={{ cursor: "pointer", fontSize: 20 }}
              />
            </Tooltip>
          </Box>
        )}
      </Stack>
    );
  };

  if (type === "delete") {
    return (
      <ConfirmationDialog
        open={open}
        onClose={onClose}
        onConfirm={onConfirm}
        title={getTitle()}
        message={
          <>
            Are you sure you want to delete <strong>{name}</strong>?<br />
            This action cannot be undone.
          </>
        }
        confirmText={dialogLoading ? "Processing..." : "Delete"}
        loading={dialogLoading}
        confirmColor='error'
      />
    );
  }

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={getTitle()}
      icon={<BusinessIcon />}
      actions={
        <>
          <ActionButton
            onClick={onClose}
            variant='outlined'
            color='primary'
            sx={{
              borderColor: "grey.400",
              color: "grey.700",
              "&:hover": {
                borderColor: "grey.600",
                backgroundColor: "grey.100",
              },
            }}
            disabled={dialogLoading}
          >
            Cancel
          </ActionButton>
          <ActionButton
            onClick={onConfirm}
            variant='contained'
            color={type === "delete" ? "error" : "primary"}
            autoFocus={type === "delete"}
            disabled={dialogLoading}
            startIcon={
              dialogLoading ? (
                <CircularProgress size={20} color='inherit' />
              ) : null
            }
            sx={{
              backgroundColor:
                type === "delete" ? "error.main" : "primary.main",
              "&:hover": {
                backgroundColor:
                  type === "delete" ? "error.dark" : "primary.dark",
              },
            }}
          >
            {dialogLoading
              ? "Processing..."
              : type.charAt(0).toUpperCase() + type.slice(1)}
          </ActionButton>
        </>
      }
    >
      <DialogContent
        id='company-dialog-description'
        sx={{
          p: 0,
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: theme.palette.grey[100],
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: theme.palette.grey[400],
            borderRadius: "3px",
            "&:hover": {
              background: theme.palette.grey[600],
            },
          },
        }}
      >
        {renderContent()}
      </DialogContent>
    </BaseDialog>
  );
};

export default CompanyDialog;
